import SecureTokenStorage from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface CreatePostData {
  title: string;
  content: string;
  isDraft?: boolean;
  scheduledAt?: string;
  subjectType?: 'PERSON' | 'BUSINESS';
  subjectId?: string;
  location?: string;
  attachments?: any[];
  tagIds?: string[];
}

export interface PostReactionData {
  type: 'LIKE' | 'HELPFUL' | 'LOVE' | 'SAD' | 'ANGRY';
}

export interface SharePostData {
  platform: string;
  message?: string;
}

class PostService {
  private static getAuthHeaders() {
    // Don't validate token on every request - let the API return 401 if invalid
    // The apiClient will handle token refresh automatically
    const tokens = SecureTokenStorage.getTokens();
    return {
      'Content-Type': 'application/json',
      Authorization: tokens?.accessToken ? `Bearer ${tokens.accessToken}` : '',
    };
  }

  /**
   * Create a new post
   */
  static async createPost(postData: CreatePostData) {
    const response = await fetch(`${API_BASE_URL}/v1/posts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(postData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create post');
    }

    return data.data;
  }

  /**
   * Get posts feed with pagination
   * Supports both page-based (backward compatible) and cursor-based pagination
   */
  static async getPostsFeed(
    options: {
      page?: number;
      cursor?: { timestamp: number; postId: string } | null;
      limit?: number;
      authorId?: string;
      search?: string;
      mode?: 'algorithmic' | 'chronological';
      cacheBust?: boolean;
    } = {}
  ) {
    const params = new URLSearchParams();

    // Use cursor if provided, otherwise use page
    if (options.cursor !== undefined && options.cursor !== null) {
      params.append('cursor', JSON.stringify(options.cursor));
    } else if (options.page) {
      params.append('page', options.page.toString());
    }

    if (options.limit) params.append('limit', options.limit.toString());
    if (options.authorId) params.append('authorId', options.authorId);
    if (options.search) params.append('search', options.search);
    if (options.mode) params.append('mode', options.mode);

    // Add cache-busting parameter to bypass cache
    if (options.cacheBust) {
      params.append('_t', Date.now().toString());
    }

    const response = await fetch(`${API_BASE_URL}/v1/posts?${params}`, {
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch posts');
    }

    return data.data;
  }

  /**
   * Check for new posts since a specific post ID
   * Supports both postId (backward compatible) and cursor
   */
  static async checkForNewPosts(
    since: string | { timestamp: number; postId: string }
  ): Promise<{
    posts: any[];
    hasNewPosts: boolean;
  }> {
    const params = new URLSearchParams();

    if (typeof since === 'string') {
      // Backward compatible: postId
      params.append('since', since);
    } else {
      // Cursor-based
      params.append('cursor', JSON.stringify(since));
    }

    const response = await fetch(
      `${API_BASE_URL}/v1/posts/check-new?${params}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.error || 'Failed to check for new posts';
      const error = new Error(errorMessage);
      // Add status code to error for better handling
      (error as any).status = response.status;
      throw error;
    }

    return data.data;
  }

  /**
   * Get a specific post by ID
   */
  static async getPost(postId: string) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}`, {
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch post');
    }

    return data.data;
  }

  /**
   * Update a post
   */
  static async updatePost(postId: string, updateData: Partial<CreatePostData>) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updateData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update post');
    }

    return data.data;
  }

  /**
   * Delete a post
   */
  static async deletePost(postId: string) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete post');
    }

    return data;
  }

  /**
   * React to a post
   */
  static async reactToPost(postId: string, reactionData: PostReactionData) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}/react`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(reactionData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to react to post');
    }

    return data.data;
  }

  /**
   * Share a post
   */
  static async sharePost(postId: string, shareData: SharePostData) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}/share`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(shareData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to share post');
    }

    return data.data;
  }

  /**
   * Add a comment to a post
   */
  static async addComment(
    postId: string,
    commentData: { content: string; parentId?: string }
  ) {
    const response = await fetch(
      `${API_BASE_URL}/v1/posts/${postId}/comments`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(commentData),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to add comment');
    }

    return data.data;
  }

  /**
   * Get comments for a post
   */
  static async getComments(postId: string, page = 1, limit = 20) {
    const response = await fetch(
      `${API_BASE_URL}/v1/posts/${postId}/comments?page=${page}&limit=${limit}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch comments');
    }

    return data.data;
  }

  /**
   * Flag a post for moderation
   */
  static async flagPost(
    postId: string,
    flagData: { reason: string; description?: string }
  ) {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}/flag`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(flagData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to flag post');
    }

    return data.data;
  }

  /**
   * Save/bookmark a post (toggle)
   */
  static async savePost(postId: string): Promise<{ saved: boolean }> {
    const response = await fetch(`${API_BASE_URL}/v1/posts/${postId}/save`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to save post');
    }

    return data.data;
  }

  /**
   * Get user's saved posts
   */
  static async getSavedPosts(page = 1, limit = 20) {
    const response = await fetch(
      `${API_BASE_URL}/v1/posts/saved?page=${page}&limit=${limit}`,
      {
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch saved posts');
    }

    return data.data;
  }
}

export default PostService;
